// ── red. Analyse — Bottom-Sheet pro Asset ────────────────────────────────
// Cash-Flow-Sicht: was hat der User echt reingesteckt, was kam zurück, was ist
// noch da. KEINE abstrakten "realisiert/unrealisiert"-Trennungen mehr.
//
// Struktur:
//   1. Title (Coin-Icon + Name + Brand-Sub)
//   2. Hero-Performance (big number, +/− gegenüber Einzahlungen)
//   3. Cash-Flow-Block (3 Stationen: eingezahlt → ausgezahlt → Bestand)
//   4. Klartext-Fazit
//   5. Footer-Disclaimer

import React, { useMemo } from 'react';
import { F, DUR } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useT } from '../../i18n/useT';
import { fmt, fmtC } from '../../utils/format';
import { Icon, Icons } from './Icons';
import { RedWordmark } from './Brand';
import CoinImage from '../CoinImage';
import Private from '../Private';
import { useMaskText } from '../../context/PrivacyContext';
import { calcAssetCashIn, calcAssetCashOut, calcAcquisitionAvgPrice } from '../../utils/portfolioCalc';

export default function AssetAnalyseSheet({
  open,
  onClose,
  asset,
  stats,
  txs,
  currentValue,
  marketData,
}) {
  const { T } = useTheme();
  const { convert, symbol, locale } = useCurrency();
  const { t } = useT();
  const mask = useMaskText();

  // Cash-Flow-Berechnung
  const cashIn = useMemo(() => calcAssetCashIn(asset.sym, txs || []), [asset.sym, txs]);
  const cashOutData = useMemo(() => calcAssetCashOut(asset.sym, txs || []), [asset.sym, txs]);
  const cashOut = cashOutData.cashOut;
  const cashOutCount = cashOutData.count;

  // Ø-Kaufpreis aus ALLEN Erwerbs-TXs + Anzahl der Käufe (für den Right-Stat).
  const avgBuy = useMemo(() => calcAcquisitionAvgPrice(asset.sym, txs || []), [asset.sym, txs]);
  const buyCount = useMemo(() => {
    if (!Array.isArray(txs)) return 0;
    const target = String(asset.sym).toUpperCase();
    return txs.filter(tx => {
      const coinSym = String(tx.coin ?? tx.sym ?? '').toUpperCase();
      const coinInSym = String(tx.coinIn ?? tx.symIn ?? '').toUpperCase();
      return (tx.typ === 'kauf' && coinSym === target && Number(tx.preis) > 0) ||
             (tx.typ === 'swap' && coinInSym === target && Number(tx.preis) > 0);
    }).length;
  }, [asset.sym, txs]);

  // Gesamt-Performance: (was du hast + was du rausgenommen hast) − was du reingesteckt hast
  const totalPerformance = (currentValue + cashOut) - cashIn;
  const renditePct = cashIn > 0 ? (totalPerformance / cashIn) * 100 : null;

  if (!open) return null;

  const perfColor = totalPerformance > 0.5 ? T.green : totalPerformance < -0.5 ? T.red : T.sub;
  const perfSign = totalPerformance >= 0 ? '+' : '−';

  let heroSubtitle;
  if (totalPerformance > 0.5) {
    heroSubtitle = t('red_aa_hero_up');
  } else if (totalPerformance < -0.5) {
    heroSubtitle = t('red_aa_hero_down');
  } else {
    heroSubtitle = t('red_aa_hero_even');
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: `redAnalyseFadeIn ${DUR.fast}ms ease`,
      }}
    >
      <style>{`
        @keyframes redAnalyseFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes redAnalyseSlideUp {
          from { transform: translateY(100%); opacity: 0.6 }
          to   { transform: translateY(0);    opacity: 1   }
        }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%', maxWidth: 480,
          maxHeight: '92vh', overflowY: 'auto',
          background: T.surface,
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderRadius: '28px 28px 0 0',
          padding: '14px 24px 26px',
          border: `1px solid ${T.line}`,
          borderBottom: 'none',
          animation: `redAnalyseSlideUp ${DUR.slow}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {/* Handle */}
        <div style={{ width: 36, height: 4, background: T.dim, borderRadius: 2, margin: '0 auto 24px' }} />

        {/* Close-Button */}
        <button
          onClick={onClose}
          aria-label={t('red_aa_close')}
          onMouseEnter={e => { e.currentTarget.style.background = T.itemHover; e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          style={{
            position: 'absolute',
            top: 18, right: 18,
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent',
            color: T.sub,
            border: `1px solid ${T.line}`,
            borderRadius: '50%',
            cursor: 'pointer',
            transition: `background ${DUR.base}ms ease, color ${DUR.base}ms ease, transform ${DUR.base}ms cubic-bezier(.34,1.5,.5,1)`,
            WebkitTapHighlightColor: 'transparent',
            padding: 0,
          }}
        >
          <Icon d={Icons.close} size={14} stroke="currentColor" sw={2.2} />
        </button>

        {/* 1. Title-Block (2 Spalten: Coin-Title links, Ø-Einkauf-Stat rechts) */}
        <div style={{
          display: 'flex', alignItems: 'stretch', justifyContent: 'space-between',
          gap: 16, marginBottom: 28,
        }}>
          {/* Left: Coin-Icon + Name, darunter mit Abstand "red. Analyse · Cash-Flow" */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <CoinImage sym={asset.sym} marketData={marketData} size={32} />
              <div style={{
                fontFamily: F.display, fontSize: 24, fontWeight: 800,
                letterSpacing: '-0.035em', color: T.text, lineHeight: 1.1,
              }}>
                {asset.name || asset.sym}
                <span style={{ marginLeft: 8, fontSize: 14, color: T.sub, fontWeight: 600, letterSpacing: '0.02em' }}>
                  {asset.sym}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <RedWordmark size={12} sub="Analyse" />
              <span style={{
                fontSize: 12, fontWeight: 500, letterSpacing: '-0.005em',
                color: T.sub,
              }}>
                · Cash-Flow
              </span>
            </div>
          </div>

          {/* Right: Ø-Einkauf-Stat — startet unterhalb des Close-Buttons,
              editorial Stat-Card-Look mit Hairline-Trennung. */}
          {avgBuy > 0 && (
            <div style={{
              borderLeft: `1px solid ${T.line}`,
              paddingLeft: 16, paddingTop: 30,
              textAlign: 'right', flexShrink: 0,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '-0.005em', color: T.mute, marginBottom: 6,
              }}>
                {t('red_aa_avg_buy')}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 3 }}>
                <span style={{
                  fontFamily: F.display, fontSize: 26, fontWeight: 800,
                  color: T.text, letterSpacing: '-0.04em', lineHeight: 1,
                }}>
                  <Private>{fmt(convert(avgBuy), locale)}</Private>
                </span>
                <span style={{
                  fontSize: 13, color: T.sub, fontWeight: 600, letterSpacing: '-0.01em',
                }}>
                  {symbol}
                </span>
              </div>
              <div style={{ fontSize: 10, color: T.mute, marginTop: 5, fontWeight: 500 }}>
                {t('red_aa_per', { sym: asset.sym })}
                {buyCount > 0 && (
                  <span style={{ color: T.sub }}>
                    {' · '}{buyCount === 1 ? t('red_aa_buys_one', { n: buyCount }) : t('red_aa_buys_many', { n: buyCount })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 2. Hero-Performance */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            fontFamily: F.display, fontSize: 40, fontWeight: 700,
            letterSpacing: '-0.04em', color: perfColor, lineHeight: 1,
            marginBottom: 8,
          }}>
            {perfSign} <Private>{fmt(convert(Math.abs(totalPerformance)), locale)}</Private> {symbol}
          </div>
          <div style={{ fontSize: 12, color: T.sub, fontWeight: 500, letterSpacing: '-0.01em' }}>
            <span style={{ color: T.text, fontWeight: 700 }}>{heroSubtitle}</span>
            {renditePct !== null && Math.abs(renditePct) > 0.5 && (
              <span> · {renditePct > 0 ? '+' : ''}{renditePct.toFixed(1)} {t('red_aa_return')}</span>
            )}
          </div>
        </div>

        {/* 3. Cash-Flow-Block: drei Stationen */}
        <div style={{ marginBottom: 22 }}>
          <SectionLabel T={T}>{t('red_aa_cashflow')}</SectionLabel>
          <div style={{ borderTop: `1px solid ${T.line}` }}>
            <FlowRow
              T={T}
              iconKey="deposited"
              label={t('red_aa_deposited')}
              sub={t('red_aa_deposited_sub')}
              value={<Private>{`${fmt(convert(cashIn), locale)} ${symbol}`}</Private>}
            />
            <FlowRow
              T={T}
              iconKey="withdrawn"
              label={t('red_aa_withdrawn')}
              sub={cashOutCount > 0
                ? (cashOutCount === 1 ? t('red_aa_sells_one', { n: cashOutCount }) : t('red_aa_sells_many', { n: cashOutCount }))
                : t('red_aa_no_sells')
              }
              value={<Private>{`${fmt(convert(cashOut), locale)} ${symbol}`}</Private>}
              valueColor={cashOut > 0.5 ? T.text : T.mute}
            />
            <FlowRow
              T={T}
              iconKey="holding"
              label={t('red_aa_holding_today')}
              sub={<><Private>{fmtC(stats.menge)}</Private> {asset.sym}</>}
              value={<Private>{`${fmt(convert(currentValue), locale)} ${symbol}`}</Private>}
              isLast
            />
          </div>
        </div>

        {/* 4. Klartext-Fazit */}
        <div style={{
          marginBottom: 18, paddingTop: 14, paddingBottom: 14,
          borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
        }}>
          <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.7 }}>
            {t('red_aa_summary_pre')}{' '}
            <Strong T={T}><Private>{`${fmt(convert(cashIn), locale)} ${symbol}`}</Private></Strong>
            {t('red_aa_summary_invested', { name: asset.name || asset.sym })}
            {cashOut > 0.5 ? (
              <>
                {' '}{t('red_aa_summary_of_which')}{' '}
                <Strong T={T}><Private>{`${fmt(convert(cashOut), locale)} ${symbol}`}</Private></Strong>
                {t('red_aa_summary_withdrawn')}
              </>
            ) : (
              <>{' '}{t('red_aa_summary_no_sells')}</>
            )}
            {' '}{t('red_aa_summary_remaining', { amount: mask(fmtC(stats.menge)), sym: asset.sym })}{' '}
            <Strong T={T}><Private>{`${fmt(convert(currentValue), locale)} ${symbol}`}</Private></Strong>
            {t('red_aa_summary_worth')}
          </div>
          <div style={{
            marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.line}`,
            fontSize: 13, color: T.text, fontWeight: 500,
          }}>
            <span style={{ color: T.sub }}>{t('red_aa_total_perf')}</span>{' '}
            <span style={{ color: perfColor, fontWeight: 800, fontFamily: F.mono, letterSpacing: '-0.01em' }}>
              {perfSign} <Private>{fmt(convert(Math.abs(totalPerformance)), locale)}</Private> {symbol}
            </span>
            {renditePct !== null && Math.abs(renditePct) > 0.5 && (
              <span style={{ color: T.sub }}>
                {' ('}{renditePct > 0 ? '+' : ''}{renditePct.toFixed(1)} {t('red_aa_return')}{')'}
              </span>
            )}
          </div>
        </div>

        {/* 5. Footer-Disclaimer */}
        <div style={{
          fontSize: 10, color: T.mute, lineHeight: 1.55, textAlign: 'center',
        }}>
          {t('red_aa_disclaimer')}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function SectionLabel({ T, children }) {
  return (
    <div style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em', color: T.mute, marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

// Cash-Flow-Row: Icon (klein, T.red Akzent), Label + Sub links, Wert rechts.
// Cash-Flow-Icons im Nav-Icon-Stil: dünne Linie (1.7), runde Enden, KEINE
// Füllung/Tönung, freistehend (kein Tile).
const FLOW_ICONS = {
  deposited: ['M12 3v11', 'M7 9l5 5 5-5', 'M4 19h16'],            // Pfeil in die Basis
  withdrawn: ['M12 14V3', 'M7 8l5-5 5 5', 'M4 19h16'],            // Pfeil aus der Basis
  holding:   ['M3 8.5A2.5 2.5 0 0 1 5.5 6H18a2 2 0 0 1 2 2v1', 'M3 8.5V17a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3a2 2 0 0 0 0 4h5'], // Wallet
};

function FlowRow({ T, iconKey, label, sub, value, valueColor, isLast }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      paddingTop: 14, paddingBottom: 14,
      borderBottom: isLast ? 'none' : `1px solid ${T.lineSoft || T.line}`,
      gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 15, flex: 1, minWidth: 0 }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={T.sub} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0 }}>
          {(FLOW_ICONS[iconKey] || []).map((d, i) => <path key={i} d={d} />)}
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, color: T.text, fontWeight: 700, letterSpacing: '-0.015em' }}>{label}</div>
          {sub && (
            <div style={{ fontSize: 11.5, color: T.sub, marginTop: 3, lineHeight: 1.4, fontWeight: 500 }}>{sub}</div>
          )}
        </div>
      </div>
      <div style={{
        fontFamily: F.mono, fontSize: 14, fontWeight: 700,
        color: valueColor || T.text, letterSpacing: '-0.01em',
        textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {value}
      </div>
    </div>
  );
}

// Inline-Highlight für Fließtext: garantiertes Leerzeichen vor/nach via NBSP
// am Anfang/Ende der Children verhindert Probleme mit JSX-Whitespace-Trimming.
function Strong({ T, color, children }) {
  return (
    <span style={{
      fontFamily: F.mono, fontWeight: 700,
      color: color || T.text, letterSpacing: '-0.01em',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}
